import each from 'lodash/each';

/**
 * Available hooks
 *
 * component.initialized(component)
 * elementInitialized(el, component)
 * element.removed(el, component)
 * message.sent(component, message)
 * message.failed(component)
 * message.received(component, response)
 * message.received(component, mutableHtmlContainer)
 * beforeElementUpdate(from, to, component)
 * element.updated(node, component)
 * message.processed(component)
 * interceptWireModelSetValue(el, value)
 * interceptWireModelAttachListener(el, directive, component, debounceIf)
 * directive(el, directive, component)
 *
 */

const markVueElements = function(parent, component) {
    each(parent.$children, (child) => {
        if (child.$el && typeof child.$el.setAttribute === 'function') {
            child.$el.setAttribute('data-vue-uid', child._uid);
            component.__vue_components[child._uid] = child;
        }
        if (child.$children) {
            markVueElements(child, component);
        }
    });
};

const restoreVueElements = function(from, to, component) {
    if (from.__vue__) {
        if (component.__vue_components[to.getAttribute('data-vue-uid')]) {
            let newVue = component.__vue_components[to.getAttribute('data-vue-uid')];
            let oldVue = from.__vue__;

            each(newVue.$props, (prop, key) => {
                if (newVue.$props[key] !== oldVue.$props[key]) {
                    oldVue.$props[key] = prop;
                }
            });

            newVue.$destroy();
            delete component.__vue_components[to.getAttribute('data-vue-uid')];
        }
    } else {
        each(to.querySelectorAll('[data-vue-uid]'), (node) => {
            let uid = node.getAttribute('data-vue-uid');
            if (component.__vue_components[uid]) {
                node.parentNode.replaceChild(component.__vue_components[uid].$el, node);
            }
        });
    }
};

export default function() {

    if (typeof window.Livewire === 'undefined') {
        throw 'Livewire Vue Plugin: window.Livewire is undefined. Make sure @livewireScripts is placed above this script include';
    }

    window.Livewire.hook('component.initialized', (component) => {
        if (!window.Vue) return;
        component.__vue_components = {};
    });

    window.Livewire.hook('message.received', (message, component) => {
        if (!window.Vue) return;
        if (!message.response.effects.html) return;

        const mountComponent = Vue.extend({
            template: message.response.effects.html,
        });

        const newVue = new mountComponent().$mount();
        markVueElements(newVue, component);
        message.response.effects.html = newVue.$el.outerHTML;
    });

    window.Livewire.hook('element.initialized', el => {
        if (el.__vue__) el.__livewire_ignore_self = true;
    });

    window.Livewire.hook('element.updating', (from, to, component) => {
        if (!window.Vue) return;
        const originalSilent = window.Vue.config.silent;
        window.Vue.config.silent = true;
        restoreVueElements(from, to, component);
        window.Vue.config.silent = originalSilent;
    });

    window.Livewire.hook('interceptWireModelSetValue', (value, el) => {
        // If it's a vue component pass down the value prop.
        if (!el.__vue__) return;

        const originalSilent = window.Vue.config.silent;
        window.Vue.config.silent = true;

        el.__vue__.$props.value = value;

        window.Vue.config.silent = originalSilent;
    });

    window.Livewire.hook('interceptWireModelAttachListener', (el, directive, component, debounceIf) => {
        if (!el.__vue__) return;

        const hasDebounceModifier = directive.modifiers.includes('debounce');
        const isLazy = directive.modifiers.includes('lazy');

        el.__vue__.$on('input', debounceIf(hasDebounceModifier || !isLazy, e => {
            const model = directive.value;
            const value = e;

            component.set(model, value);
        }, directive.durationOr(150)));
    });
}
